import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAccessToken } from '../auth/use-access-token.js';
import { useServices } from '../api/use-catalogue.js';
import { useSlot } from '../api/use-slot.js';
import { chooseAthlete, coachBook, isNewAthlete, useChosenAthlete } from '../api/coach-booking.js';
import { useMeContext } from '../api/me-provider.js';
import { ApiError } from '../api/client.js';
import { ChosenAthlete } from '../components/chosen-athlete.js';
import { ShareFamilyLink } from '../components/share-family-link.js';
import { WaitNote } from '../components/wait-note.js';
import { BusyLabel } from '../components/busy-label.js';
import { hourProducts } from '../components/hour-products.js';

/**
 * The MPC coach on one hour, booking for the athlete chosen on Book (stage B;
 * canvas row 10, approved 16 October).
 *
 * "What kind of lesson" as the parent sees it - from the family's catalogue,
 * by the family's country, because where they live decides what they can buy
 * - unless the hour is already claimed, which settles it. Then Book: one
 * athlete, no cart. Booked shows in place, as the parent's Booked does, with
 * See my lessons.
 *
 * A new athlete (stage C; canvas row 11): their family has no country yet, so
 * the lessons are the coach's own country's - where the coach teaches - and
 * the family takes the country of the lesson booked. Booked then says where
 * the invitation went - or, with no email to send it to (ruled 23 October),
 * offers Share link - and Book holds the athlete just made, so a second hour
 * books them rather than starting them again.
 */
export function CoachBookHourRoute() {
  const { slotId } = useParams();
  const getToken = useAccessToken();
  const athlete = useChosenAthlete();
  const services = useServices();
  const { state: meState } = useMeContext();
  const { state: slotState } = useSlot(slotId, null, athlete && !isNewAthlete(athlete) ? athlete.client_id : null);
  const [productId, setProductId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [booked, setBooked] = useState<{
    start: string;
    name: string;
    invited: string | null;
    /** The family to share the sign-up link for, when there was no email to send it to. */
    share: string | null;
  } | null>(null);

  if (booked) {
    return (
      <>
        <h2>Booked</h2>
        <div className="fact">
          <span className="label">{when(booked.start)}</span>
          <span className="value">{booked.name}</span>
        </div>
        {booked.invited ? (
          <div className="fact">
            <span className="label">Invited</span>
            <span className="value">{booked.invited}</span>
          </div>
        ) : null}
        {booked.share ? <ShareFamilyLink adultAccountId={booked.share} /> : null}
        <Link className={booked.share ? 'secondary' : 'primary'} to="/lessons">
          See my lessons
        </Link>
      </>
    );
  }

  if (!athlete) {
    return (
      <section className="panel">
        <Link className="primary" to="/booking">
          Back to the calendar
        </Link>
      </section>
    );
  }

  if (slotState.status === 'loading' || services.status === 'loading') return <WaitNote className="empty">Checking…</WaitNote>;
  if (slotState.status !== 'ready') {
    return (
      <section className="panel">
        <h2>That hour has gone</h2>
        <Link className="primary" to="/booking">
          Back to the calendar
        </Link>
      </section>
    );
  }

  const slot = slotState.slot;
  const coachCountry = meState.status === 'ready' ? meState.me.profile?.country : undefined;
  const country = isNewAthlete(athlete) ? coachCountry : athlete.country;
  const sold = services.status === 'ready' ? services.value.filter((service) => service.country === country) : [];
  // A Lessons hour offers Assessment too; an Assessments hour only that (032).
  // An athlete who has an Assessment already is not offered another (one per athlete, ever).
  const products = hourProducts(sold, slot.for_assessments).filter(
    (entry) => !(entry.isAssessment && !isNewAthlete(athlete) && slot.assessed_ids.includes(athlete.client_id)),
  );
  const claimed = slot.claimed_tier_id
    ? products.find((entry) => entry.product.tier_id === slot.claimed_tier_id)
    : slot.for_assessments && products.length === 1
      ? products[0]
      : undefined;
  const selected = claimed ?? products.find((entry) => entry.product.tier_id === productId);

  async function book() {
    if (!selected || !athlete || saving) return;
    setSaving(true);
    setProblem(null);
    try {
      const hour = {
        slot_id: slot.slot_id,
        service_id: selected.serviceId,
        capacity_tier_id: selected.product.tier_id,
      };
      const result = await coachBook(
        getToken,
        isNewAthlete(athlete)
          ? {
              ...hour,
              new_athlete: {
                athlete_first_name: athlete.athlete_first_name,
                athlete_last_name: athlete.athlete_last_name,
                parent_first_name: athlete.parent_first_name,
                parent_last_name: athlete.parent_last_name,
                parent_phone: athlete.parent_phone ?? '',
                parent_email: athlete.parent_email,
              },
            }
          : { ...hour, athlete_client_id: athlete.client_id },
      );
      if (isNewAthlete(athlete)) {
        chooseAthlete({
          client_id: result.athlete_client_id,
          name: athlete.name,
          birth_year: null,
          parent_name: `${athlete.parent_first_name} ${athlete.parent_last_name}`,
          adult_account_id: '',
          country: country ?? null,
        });
      }
      setBooked({
        start: result.slot_start,
        name: athlete.name,
        invited: result.invited_email,
        share: result.share_link ? result.adult_account_id : null,
      });
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : '';
      setProblem(
        code === 'already_booked'
          ? `${athlete.name} is already booked into this hour.`
          : code === 'slot_not_bookable'
            ? 'That hour is no longer bookable.'
            : 'That did not book. Try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <Link className="back" to="/booking" aria-label="Back to the calendar">
          ‹
        </Link>
        <div>
          <h2>{when(slot.slot_start)}</h2>
          <span className="detail">
            Coach {slot.coach_name} · {slot.facility_name}
          </span>
        </div>
      </div>

      <ChosenAthlete name={athlete.name} />

      {claimed ? (
        <div className="panel">
          <span className="section-label">{claimed.product.name}</span>
        </div>
      ) : (
        <>
          <span className="section-label">What kind of lesson</span>
          {products.map((entry) => (
            <label key={entry.product.tier_id} className="choice">
              <input
                type="radio"
                name="product"
                value={entry.product.tier_id}
                checked={productId === entry.product.tier_id}
                onChange={() => setProductId(entry.product.tier_id)}
              />
              <span className="choice-text">
                <span className="name">{entry.product.name}</span>
                {entry.named ? <span className="detail">{entry.serviceName}</span> : null}
              </span>
              <span className="places">
                {entry.product.capacity === 1 ? '1 place' : `up to ${entry.product.capacity}`}
              </span>
            </label>
          ))}
        </>
      )}

      {problem ? (
        <p className="form-note" role="alert">
          {problem}
        </p>
      ) : null}

      <button type="button" className="primary" onClick={() => void book()} disabled={!selected || saving} aria-busy={saving}>
        <BusyLabel busy={saving} working="Booking…">
          Book
        </BusyLabel>
      </button>
    </>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
